import {
  type Column,
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  type OnChangeFn,
  type SortingState,
  type Table as TableType,
  useReactTable,
  type VisibilityState,
} from "@tanstack/react-table";
import { ChevronDown, PlusCircle } from "lucide-react";
import React, { useEffect, useState } from "react";
import { twJoin } from "tailwind-merge";

import { Badge } from "./badge";
import { Button } from "./button";
import { Checkbox } from "./checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./table";

function readStringArrayFilterValue(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  if (value.every((item): item is string => typeof item === "string")) {
    return value;
  }
  return undefined;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  toolbar,
  zebraStyle = false,
  noBorders = false,
  columnVisibility,
  setColumnVisibility,
  defaultSorting,
  id,
  expandedContent,
  className,
}: {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  toolbar?: (table: TableType<TData>) => React.ReactNode;
  zebraStyle?: boolean;
  noBorders?: boolean;
  columnVisibility?: VisibilityState;
  setColumnVisibility?: OnChangeFn<VisibilityState>;
  defaultSorting?: SortingState;
  id: string;
  expandedContent?: (row: TData) => React.ReactNode;
  className?: string;
}) {
  const [sorting, setSorting] = useState<SortingState>(defaultSorting ?? []);
  const table = useReactTable({
    data,
    columns,
    state: {
      columnVisibility,
      sorting,
    },
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className={twJoin("space-y-4 border-border", className)} id={id}>
      <div className="flex w-full items-center justify-between gap-2.5">
        <div className="flex items-center gap-2.5">{toolbar?.(table)}</div>
      </div>
      <div className="border-border overflow-x-auto rounded-xl border">
        <Table className="w-full" id={`${id}-table`}>
          <TableHeader className={zebraStyle ? "bg-muted" : undefined}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const Container = header.column.getCanSort()
                    ? "button"
                    : "div";
                  return (
                    <TableHead
                      className={twJoin(
                        zebraStyle &&
                          "subtitle-sm text-nowrap rounded-b-none! text-foreground last:border-none",
                        zebraStyle && !noBorders && "border-border border-r"
                      )}
                      key={header.id}
                    >
                      <Container
                        className="flex w-full items-center gap-2"
                        onClick={
                          header.column.getCanSort()
                            ? () => header.column.toggleSorting()
                            : undefined
                        }
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                        {header.column.getIsSorted() && (
                          <div className="bg-muted grid size-5 place-items-center rounded-sm">
                            <ChevronDown
                              className={twJoin(
                                "size-4 text-foreground transition-transform",
                                header.column.getIsSorted() === "desc" &&
                                  "rotate-180"
                              )}
                            />
                          </div>
                        )}
                      </Container>
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <React.Fragment key={row.id}>
                  <TableRow
                    className={twJoin(
                      zebraStyle && "bg-muted odd:bg-background",
                      noBorders && "border-none"
                    )}
                    data-state={row.getIsSelected() && "selected"}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        className={twJoin(
                          "subtitle-sm text-foreground",
                          zebraStyle && "text-nowrap p-4",
                          zebraStyle &&
                            !noBorders &&
                            "border-border border-r last:border-none"
                        )}
                        key={cell.id}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  {expandedContent?.(row.original)}
                </React.Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell
                  className="h-24 text-center"
                  colSpan={columns.length}
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export function DataTableFacetedFilter<TData, TValue>({
  column,
  title,
  options,
  placeholder = "Search...",
  singleSelect = false,
}: {
  column?: Column<TData, TValue>;
  title?: string;
  options: {
    label: string;
    value: string;
  }[];
  placeholder?: string;
  singleSelect?: boolean;
}) {
  const facets = column?.getFacetedUniqueValues();
  const currentFilterValue = readStringArrayFilterValue(
    column?.getFilterValue()
  );

  const [selectedValues, setSelectedValues] = useState<Set<string>>(
    () => new Set(currentFilterValue ?? [])
  );

  useEffect(() => {
    if (currentFilterValue) {
      setSelectedValues(new Set(currentFilterValue));
    } else {
      setSelectedValues(new Set());
    }
  }, [currentFilterValue]);

  const handleSelect = (value: string) => {
    const newSelectedValues = new Set(selectedValues);
    if (singleSelect) {
      // For single select, only allow one value
      if (newSelectedValues.has(value)) {
        newSelectedValues.clear();
      } else {
        newSelectedValues.clear();
        newSelectedValues.add(value);
      }
    } else if (newSelectedValues.has(value)) {
      // For multi-select, toggle the value
      newSelectedValues.delete(value);
    } else {
      newSelectedValues.add(value);
    }
    setSelectedValues(newSelectedValues);

    setTimeout(() => {
      const filterValues = Array.from(newSelectedValues);
      column?.setFilterValue(filterValues.length ? filterValues : undefined);
      column?.columnDef?.meta?.onFilterChange?.(filterValues);
    }, 0);
  };

  return (
    <div className="flex flex-col gap-1.5">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            className="inline-flex h-8 w-fit"
            size="sm"
            type="button"
            variant="outline"
          >
            <div className="flex w-full items-center gap-1.5">
              <PlusCircle className="size-4 shrink-0" />
              <span className="truncate">{title}</span>
              {selectedValues.size > 0 && (
                <Badge
                  className="ml-1 shrink-0 rounded-md px-1 font-normal"
                  variant="secondary"
                >
                  {selectedValues.size}
                </Badge>
              )}
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-60 p-0">
          <Command>
            <CommandInput placeholder={placeholder} />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                {options.map((option) => {
                  const isSelected = selectedValues.has(option.value);
                  return (
                    <CommandItem
                      className="flex items-center gap-2"
                      key={option.value}
                      onSelect={() => handleSelect(option.value)}
                      value={option.value}
                    >
                      <Checkbox
                        checked={isSelected}
                        className="pointer-events-none"
                      />
                      <span>{option.label}</span>
                      {facets?.get(option.value) && (
                        <span className="caption ml-auto flex h-4 w-4 items-center justify-center font-mono">
                          {facets.get(option.value)}
                        </span>
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
              {selectedValues.size > 0 && (
                <>
                  <CommandSeparator />
                  <CommandGroup>
                    <CommandItem
                      className="justify-center text-center"
                      onSelect={() => {
                        setSelectedValues(new Set());
                        setTimeout(() => {
                          column?.setFilterValue(undefined);
                        }, 0);
                      }}
                    >
                      Clear filters
                    </CommandItem>
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selectedValues.size > 0 && (
        <div className="flex flex-wrap gap-1">
          {options
            .filter((option) => selectedValues.has(option.value))
            .map((option) => (
              <Badge
                className="body-sm rounded-md px-2 py-1"
                key={option.value}
                variant="secondary"
              >
                {option.label}
              </Badge>
            ))}
        </div>
      )}
    </div>
  );
}
